'use client';

import { Box } from '@terra/ui-styled-system';
import { MarkdownPreview } from '../../markdown-preview';
import { useRef, useEffect, useState } from 'react';

type MarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  height?: string | number;
  placeholder?: string;
  readOnly?: boolean;
};

/**
 * MarkdownEditor with HTTP syntax highlighting
 * Dynamically measures the rendered markdown to perfectly align textarea with preview
 */
export function MarkdownEditor({
  value,
  onChange,
  height = '100%',
  placeholder = 'Enter text...',
  readOnly = false,
}: MarkdownEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [textareaStyles, setTextareaStyles] = useState<{
    paddingTop: string;
    paddingLeft: string;
    paddingRight: string;
    paddingBottom: string;
    fontSize: string;
    lineHeight: string;
    fontFamily: string;
  } | null>(null);

  // Wrap content in HTTP code block for syntax highlighting in preview
  const wrappedValue = `\`\`\`http\n${value}\n\`\`\``;

  // Dynamically calculate exact padding and styles from rendered elements
  useEffect(() => {
    const preview = previewRef.current;
    if (!preview) return;

    // Wait for markdown to render
    const timer = setTimeout(() => {
      const preElement = preview.querySelector('pre');
      const codeElement = preview.querySelector('code');

      if (!preElement || !codeElement) return;

      const preStyle = window.getComputedStyle(preElement);
      const codeStyle = window.getComputedStyle(codeElement);

      // Calculate total horizontal padding (pre + code padding/margin)
      const prePaddingLeft = parseFloat(preStyle.paddingLeft);
      const prePaddingRight = parseFloat(preStyle.paddingRight);
      const codePaddingLeft = parseFloat(codeStyle.paddingLeft) || 0;
      const codePaddingRight = parseFloat(codeStyle.paddingRight) || 0;
      const codeMarginLeft = parseFloat(codeStyle.marginLeft) || 0;
      const codeMarginRight = parseFloat(codeStyle.marginRight) || 0;

      const totalPaddingLeft =
        prePaddingLeft + codePaddingLeft + codeMarginLeft;
      const totalPaddingRight =
        prePaddingRight + codePaddingRight + codeMarginRight;

      const codeFontSize = codeStyle.fontSize;
      const codeFontFamily = codeStyle.fontFamily;

      // CRITICAL: Preview wraps content in ```http\n{content}\n```
      // This adds 2 extra lines. We need to add equivalent padding to textarea.
      const codeLineHeight = parseFloat(codeStyle.lineHeight);

      // Top padding: add line height to skip past the ```http line
      const totalPaddingTop = parseFloat(preStyle.paddingTop) + codeLineHeight;

      // Bottom padding: account for closing ``` line
      const totalPaddingBottom =
        parseFloat(preStyle.paddingBottom) + codeLineHeight;

      setTextareaStyles({
        paddingTop: `${totalPaddingTop}px`,
        paddingLeft: `${totalPaddingLeft}px`,
        paddingRight: `${totalPaddingRight}px`,
        paddingBottom: `${totalPaddingBottom}px`,
        fontSize: codeFontSize,
        lineHeight: codeStyle.lineHeight,
        fontFamily: codeFontFamily,
      });
    }, 50);

    return () => clearTimeout(timer);
  }, [wrappedValue]);

  // Synchronize scrolling between textarea and preview
  useEffect(() => {
    if (!textareaStyles) return;

    const textarea = textareaRef.current;
    const preview = previewRef.current;

    if (!textarea || !preview) return;

    const handleScroll = () => {
      preview.scrollTop = textarea.scrollTop;
      preview.scrollLeft = textarea.scrollLeft;
    };

    textarea.addEventListener('scroll', handleScroll);
    return () => textarea.removeEventListener('scroll', handleScroll);
  }, [textareaStyles]);

  return (
    <Box position="relative" h={height} w="full" overflow="hidden">
      {/* Syntax highlighted preview (behind) */}
      <Box
        ref={previewRef}
        position="absolute"
        inset="0"
        overflow="auto"
        pointerEvents="none"
        zIndex={0}
        css={{
          // Keep MarkdownPreview default behavior
          '& pre': {
            whiteSpace: 'pre-wrap !important',
            wordWrap: 'break-word !important',
            overflowWrap: 'break-word !important',
            wordBreak: 'break-all !important',
          },
          '& code': {
            whiteSpace: 'pre-wrap !important',
            wordWrap: 'break-word !important',
            overflowWrap: 'break-word !important',
            wordBreak: 'break-all !important',
          },
        }}
      >
        <MarkdownPreview source={wrappedValue} compact />
      </Box>

      {/* Editable textarea (on top, transparent) */}
      {textareaStyles && (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          placeholder={placeholder}
          spellCheck={false}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            paddingTop: textareaStyles.paddingTop,
            paddingLeft: textareaStyles.paddingLeft,
            paddingRight: textareaStyles.paddingRight,
            paddingBottom: textareaStyles.paddingBottom,
            fontSize: textareaStyles.fontSize,
            lineHeight: textareaStyles.lineHeight,
            fontFamily: textareaStyles.fontFamily,
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'transparent',
            caretColor: 'var(--colors-fg-default)',
            resize: 'none',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            wordBreak: 'break-all',
            margin: 0,
            boxSizing: 'border-box',
          }}
        />
      )}
    </Box>
  );
}
